import "./EditNoShowReportPopup.scss";
import { useEffect, useState } from "react";

import { toast } from "react-toastify";
import Popup from "../../../hoc/Popup/Popup";
import { ICourt, INoShowReport } from "../../../types/api";
import {
  activateNoShowReport,
  resolveNoShowReport,
  updateNoShowReportComment,
} from "../../../api/noShowReports/noShowReports";

interface IEditNoShowReportPopupProps {
  showPopup: boolean;
  onClose: () => void;
  onConfirm: () => void;
  noShowReport: INoShowReport | undefined;
  courts: ICourt[];
}

function EditNoShowReportPopup(props: IEditNoShowReportPopupProps) {
  const { showPopup, onClose, onConfirm, noShowReport, courts } = props;
  const [newComment, setNewComment] = useState(noShowReport?.comment || "");
  const handleClose = () => {
    onClose();
  };

  const [noShowReportToDisplay, setNoShowReportToDisplay] = useState<
    INoShowReport | undefined
  >(noShowReport);
  useEffect(() => {
    setNoShowReportToDisplay(noShowReport);
  }, [noShowReport]);
  const handleUpdateNoShowReportComment = async (newComment: string) => {
    if (!noShowReportToDisplay) {
      toast.error("No-show saknas.");
      return;
    }
    try {
      const updateResponse = await updateNoShowReportComment(
        noShowReportToDisplay.PK,
        noShowReportToDisplay.date,
        newComment
      );
      if (updateResponse && !("message" in updateResponse)) {
        setNoShowReportToDisplay(updateResponse);
        setNewComment("");
        onConfirm();
      }
      toast.success("Kommentar uppdaterad.");
    } catch (error: any) {
      toast.error(`Kunde inte uppdatera kommentar: ${error.message}`);
    }
  };

  const handleResolveNoShowReport = async () => {
    if (!noShowReportToDisplay) {
      toast.error("No-show saknas.");
      return;
    }
    try {
      const updateResponse = await resolveNoShowReport(
        noShowReportToDisplay.PK,
        noShowReportToDisplay.date,
        noShowReportToDisplay.member_id
      );
      if (updateResponse && !("message" in updateResponse)) {
        setNoShowReportToDisplay(updateResponse);
        onConfirm();
      }
      toast.success("No-show löst.");
    } catch (error: any) {
      toast.error(`Kunde inte inaktivera no-show: ${error.message}`);
    }
  };

  const handleActivateNoShowReport = async () => {
    if (!noShowReportToDisplay) {
      toast.error("No-show saknas.");
      return;
    }

    try {
      const updateResponse = await activateNoShowReport(
        noShowReportToDisplay.PK,
        noShowReportToDisplay.date,
        noShowReportToDisplay.member_id
      );
      if (updateResponse && !("message" in updateResponse)) {
        setNoShowReportToDisplay(updateResponse);
        onConfirm();
      }

      toast.success("No-show aktiverad.");
    } catch (error: any) {
      toast.error(`Kunde inte aktivera no-show: ${error.message}`);
    }
  };
  // Basic date/time
  const date = noShowReportToDisplay?.date?.slice(0, 10) || "-";
  const time = noShowReportToDisplay?.date?.slice(11, 16) || "-";

  // Court name
  const courtName = noShowReportToDisplay?.court_id
    ? courts.find((c) => c.court_id === noShowReportToDisplay.court_id)?.name ||
      "-"
    : "-";

  // Slot
  const slotDate = noShowReportToDisplay?.slot_datetime_start
    ? noShowReportToDisplay.slot_datetime_start.slice(0, 10)
    : null;
  const slotTime = noShowReportToDisplay?.slot_datetime_start
    ? noShowReportToDisplay.slot_datetime_start.slice(11, 16)
    : null;

  // Status & reason
  const status = noShowReportToDisplay?.status || "-";
  const resolvedReason = noShowReportToDisplay?.resolved_reason
    ? `- ${noShowReportToDisplay.resolved_reason}`
    : "";

  // Type
  const type = noShowReportToDisplay?.type || "-";
  const comment = noShowReportToDisplay?.comment || "Ingen kommentar";
  return (
    <Popup
      showPopup={showPopup}
      onClose={handleClose}
      leftTopElement={
        <span className="text-m">
          Redigera no-show {date}, {time}
        </span>
      }
    >
      <div className="general-popup-wrapper">
        <form
          className="general-popup-form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              {/* Date */}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Datum</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{date || "-"}</span>
                </div>
              </div>

              {/* Time */}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Tid</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{time || "-"}</span>
                </div>
              </div>

              {/* Court */}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Bana</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{courtName}</span>
                </div>
              </div>

              {/* Booked slot date/time */}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Bokad tid</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {slotDate && slotTime ? `${slotDate}, ${slotTime}` : "-"}
                  </span>
                </div>
              </div>

              {/* Type */}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Typ</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{type}</span>
                </div>
              </div>

              {/* Status (with resolved reason if applicable) */}
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Status</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>
                    {status} {(status === "RESOLVED" || status === "REMOVED") && resolvedReason}
                  </span>
                </div>
              </div>
              <div className="general-popup-flex-between">
                <span className="text-m primary-text">Kommentar</span>
                <div className="text-m primary-text text-svg-wrapper">
                  <span>{comment}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="general-popup-form-group">
            <div className="general-popup-form-field">
              <label htmlFor="comment" className="text-s">
                Kommentar
              </label>
              <input
                type="text"
                name="comment"
                id="comment"
                className="text-m"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
              />
            </div>
          </div>
          <div className="general-popup-bottom-buttons-wrapper flex-end">
            <button
              onClick={() => handleUpdateNoShowReportComment(newComment)}
              className="text-m"
            >
              Lägg till kommentar
            </button>
          </div>
          <div className="general-popup-bottom-buttons-wrapper">
            {noShowReportToDisplay?.status === "RESOLVED" || noShowReportToDisplay?.status === "REMOVED" ? (
              <button onClick={handleActivateNoShowReport} className="text-m">
                Aktivera no-show
              </button>
            ) : (
              <button onClick={handleResolveNoShowReport} className="text-m">
                Inaktivera no-show
              </button>
            )}
          </div>
        </form>
      </div>
      {/* <HandleConfirmationPopup
        showPopup={showHandleDeleteAdminPopup}
        onClose={() => setShowHandleDeleteAdminPopup(false)}
        onConfirm={() => handleDeleteAdmin(admin?.member_id || "")}
        questionText={`Är du säker på att radera ${admin?.member_id}?`}
        confirmText="Radera admin"
        denyText="Nej"
      /> */}
    </Popup>
  );
}

export default EditNoShowReportPopup;
